import { Daycare } from '@/types/Daycare'
import { Role } from '@/types/Role'
import { DaycareRights } from '@/types/Context'

export function defineDaycareRights(daycare: Daycare, role: Role): DaycareRights {
    const hasStaffAccess: boolean = role === Role.cald || role === Role.trustee || daycare.staffAccess === true
    const hasNewsAccess: boolean = role === Role.cald || role === Role.trustee || daycare.newsAccess === true
    const hasSuggestAccess: boolean = role === Role.cald || role === Role.trustee || daycare.suggestAccess === true
    const hasTaskNoteAccess: boolean = role === Role.cald || role === Role.trustee || daycare.taskNoteAccess === true
    const hasMealAccess: boolean = role === Role.cald || role === Role.trustee || daycare.mealAccess === true

    const canWriteParamsDaycare: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_params_daycare === 2 : false)
    const canReadParamsDaycare: boolean =
        role === Role.cald ||
        role === Role.trustee ||
        (daycare.staffLink ? daycare.staffLink.rw_params_daycare >= 1 : false)

    const canWriteStaffWorks: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_staff_work === 2 : false)
    const canReadStaffWorks: boolean =
        role === Role.cald ||
        role === Role.trustee ||
        (daycare.staffLink ? daycare.staffLink.rw_staff_work >= 1 : false)

    const canWriteStaffRights: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffAccess ? (daycare.staffLink ? daycare.staffLink.rw_staffs === 2 : false) : false)
    const canReadStaffRights: boolean =
        role === Role.cald ||
        role === Role.trustee ||
        (daycare.staffAccess ? (daycare.staffLink ? daycare.staffLink.rw_staffs >= 1 : false) : false)

    const canReadFamilies: boolean =
        role === Role.cald || role === Role.trustee || (daycare.staffLink ? daycare.staffLink.rw_families >= 1 : false)
    const canWriteFamilies: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_families === 2 : false)

    const canReadChild: boolean =
        role === Role.cald || role === Role.trustee || (daycare.staffLink ? daycare.staffLink.rw_child >= 1 : false)
    const canWriteChild: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_child === 2 : false)

    const canReadContracts: boolean =
        role === Role.cald ||
        role === Role.trustee ||
        (daycare.staffLink ? daycare.staffLink.rw_regular_slot >= 1 : false)
    const canWriteContracts: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_regular_slot === 2 : false)

    const canWriteOccasionalSlot: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink
            ? daycare.staffLink.rw_request_slot === 2 && daycare.staffLink.rw_occasional_slot === 2
            : false)
    const canReadOccasionalSlot: boolean =
        role === Role.cald ||
        role === Role.parent ||
        role === Role.trustee ||
        (daycare.staffLink
            ? daycare.staffLink.rw_request_slot >= 1 && daycare.staffLink.rw_occasional_slot >= 1
            : false)

    const canReadNewsFeed: boolean =
        role === Role.cald ||
        role === Role.parent ||
        role === Role.trustee ||
        (daycare.staffLink ? daycare.staffLink.rw_news >= 1 : false)
    const canWriteNewsFeed: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_news === 2 : false)

    const canReadEvents: boolean =
        role === Role.cald ||
        role === Role.parent ||
        role === Role.trustee ||
        (daycare.staffLink ? daycare.staffLink.rw_event_days >= 1 : false)
    const canWriteEvents: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_event_days === 2 : false)

    const canReadClosingDays: boolean =
        role === Role.cald ||
        role === Role.trustee ||
        (daycare.staffLink ? daycare.staffLink.rw_closing_days >= 1 : false)
    const canWriteClosingDays: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_closing_days === 2 : false)

    const canAddNewsWithoutValidation: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.w_add_posts : false)

    const canReadSuggestions: boolean =
        role === Role.cald ||
        role === Role.trustee ||
        (daycare.staffLink ? daycare.staffLink.rw_suggestions >= 1 : false)
    const canWriteSuggestions: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_suggestions === 2 : false)

    const canReadTaskNote: boolean =
        role === Role.cald || role === Role.trustee || (daycare.staffLink ? daycare.staffLink.rw_task_note >= 1 : false)
    const canWriteTaskNote: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_task_note === 2 : false)

    const canReadMeal: boolean =
        role === Role.cald || role === Role.trustee || (daycare.staffLink ? daycare.staffLink.rw_meal >= 1 : false)
    const canWriteMeal: boolean =
        role === Role.cald ||
        (role === Role.trustee && daycare.trusteeWrite) ||
        (daycare.staffLink ? daycare.staffLink.rw_meal === 2 : false)

    return {
        hasStaffAccess,
        hasNewsAccess,
        hasSuggestAccess,
        hasTaskNoteAccess,
        hasMealAccess,
        canWriteParamsDaycare,
        canReadParamsDaycare,
        canWriteStaffWorks,
        canReadStaffWorks,
        canReadStaffRights,
        canWriteStaffRights,
        canReadFamilies,
        canWriteFamilies,
        canReadChild,
        canWriteChild,
        canReadContracts,
        canWriteContracts,
        canReadOccasionalSlot,
        canWriteOccasionalSlot,
        canReadNewsFeed,
        canWriteNewsFeed,
        canAddNewsWithoutValidation,
        canReadEvents,
        canWriteEvents,
        canReadClosingDays,
        canWriteClosingDays,
        canReadSuggestions,
        canWriteSuggestions,
        canReadTaskNote,
        canWriteTaskNote,
        canReadMeal,
        canWriteMeal
    }
}

export const noDaycareRights: DaycareRights = {
    hasStaffAccess: false,
    hasNewsAccess: false,
    hasSuggestAccess: false,
    hasTaskNoteAccess: false,
    hasMealAccess: false,
    canWriteParamsDaycare: false,
    canReadParamsDaycare: false,
    canWriteStaffWorks: false,
    canReadStaffWorks: false,
    canReadStaffRights: false,
    canWriteStaffRights: false,
    canReadFamilies: false,
    canWriteFamilies: false,
    canReadChild: false,
    canWriteChild: false,
    canReadContracts: false,
    canWriteContracts: false,
    canReadOccasionalSlot: false,
    canWriteOccasionalSlot: false,
    canReadNewsFeed: false,
    canWriteNewsFeed: false,
    canAddNewsWithoutValidation: false,
    canReadEvents: false,
    canWriteEvents: false,
    canReadClosingDays: false,
    canWriteClosingDays: false,
    canReadSuggestions: false,
    canWriteSuggestions: false,
    canReadTaskNote: false,
    canWriteTaskNote: false,
    canReadMeal: false,
    canWriteMeal: false
}
